import React, { useRef, useEffect, useState, useCallback } from 'react';
import useScoreSubmit from '../hooks/useScoreSubmit';
import ScoreboardPanel from '../components/ScoreboardPanel';

// ─── Audio: retro blips via Web Audio API (no files, no libs) ───────────────
let audioCtx = null;
const getAudio = () => {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
};
const playBlip = (freq = 440, type = 'square', duration = 0.08, vol = 0.18) => {
  try {
    const ctx = getAudio();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.5, ctx.currentTime + duration);
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch (_) {}
};
const playKill    = () => playBlip(520, 'square', 0.09, 0.2);
const playCombo   = (n) => playBlip(300 + n * 40, 'sawtooth', 0.12, 0.15);
const playMutant  = () => { playBlip(180, 'sawtooth', 0.18, 0.25); };
const playLevelUp = () => {
  [600, 750, 900].forEach((f, i) =>
    setTimeout(() => playBlip(f, 'square', 0.14, 0.22), i * 120)
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────
const LEVELS = [
  { goal: 500,      maxAliens: 8,  spawnRate: 800 },
  { goal: 1500,     maxAliens: 12, spawnRate: 600 },
  { goal: 3500,     maxAliens: 16, spawnRate: 450 },
  { goal: 7000,     maxAliens: 22, spawnRate: 350 },
  { goal: Infinity, maxAliens: 30, spawnRate: 250 },
];

const ALIEN_TYPES = {
  normal: { color: '#00ffcc', speed: 1.2, points: 10, size: 14, name: 'Blob'  },
  fast:   { color: '#ff00aa', speed: 2.5, points: 20, size: 12, name: 'Spark' },
  split:  { color: '#ffff00', speed: 1.5, points: 15, size: 16, name: 'Venom', splits: true },
  shield: { color: '#4488ff', speed: 1.0, points: 25, size: 18, name: 'Titan', hits: 2 },
  chaos:  { color: '#ff4400', speed: 1.8, points: 40, size: 15, name: 'Omega' },
};

// ─── Game Classes ─────────────────────────────────────────────────────────────
class FloatingText {
  constructor(x, y, text, color = '#ffffff') {
    this.x = x; this.y = y; this.text = text; this.color = color;
    this.life = 55; this.vy = -1.4;
  }
  update() { this.y += this.vy; this.vy *= 0.97; this.life -= 1; }
  draw(ctx) {
    const alpha = Math.min(1, this.life / 30);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.color;
    ctx.font = `bold ${14 + (55 - this.life) * 0.15}px monospace`;
    ctx.textAlign = 'center';
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 8;
    ctx.fillText(this.text, this.x, this.y);
    ctx.restore();
  }
}

class Particle {
  constructor(x, y, color, vx, vy) {
    this.x = x; this.y = y;
    this.vx = vx ?? (Math.random() - 0.5) * 6;
    this.vy = vy ?? (Math.random() - 0.5) * 6;
    this.life = 35 + Math.random() * 20;
    this.color = color;
    this.size = 3 + Math.random() * 4;
  }
  update() {
    this.x += this.vx; this.y += this.vy;
    this.vx *= 0.96;  this.vy *= 0.96;
    this.life -= 1;   this.size *= 0.97;
  }
  draw(ctx) {
    ctx.save();
    ctx.globalAlpha = this.life / 50;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(this.x, this.y, Math.max(0.1, this.size), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

class Alien {
  constructor(x, y, type = 'normal') {
    this.x = x; this.y = y; this.type = type;
    this.vx = 0; this.vy = 0;
    this.angle = Math.random() * Math.PI * 2;
    this.pulse = 0;
    this.hits = ALIEN_TYPES[type].hits || 1;
    this.size = ALIEN_TYPES[type].size;
  }
  update(cx, cy, radius, delta, overload) {
    const dx = cx - this.x, dy = cy - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const speed = ALIEN_TYPES[this.type].speed * (1 + overload / 60);
    if (dist > 40) {
      this.vx = (dx / dist) * speed;
      this.vy = (dy / dist) * speed;
    }
    this.vx += (Math.random() - 0.5) * 0.6;
    this.vy += (Math.random() - 0.5) * 0.6;
    const vel = Math.hypot(this.vx, this.vy);
    if (vel > speed * 1.5) { this.vx = (this.vx / vel) * speed * 1.5; this.vy = (this.vy / vel) * speed * 1.5; }
    this.x += this.vx * (delta / 16);
    this.y += this.vy * (delta / 16);
    const d2 = Math.hypot(this.x - cx, this.y - cy);
    if (d2 > radius - 20) {
      const nx = (this.x - cx) / d2, ny = (this.y - cy) / d2;
      this.x = cx + nx * (radius - 20);
      this.y = cy + ny * (radius - 20);
      this.vx *= -0.4; this.vy *= -0.4;
    }
    this.pulse = Math.sin(Date.now() / 150) * 2;
    this.angle += 0.02;
  }
  draw(ctx) {
    const t = ALIEN_TYPES[this.type];
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.angle);
    ctx.shadowColor = t.color; ctx.shadowBlur = 22;
    ctx.fillStyle = t.color;
    ctx.beginPath(); ctx.arc(0, 0, this.size + this.pulse, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#112233';
    ctx.beginPath(); ctx.arc(0, 0, this.size * 0.55, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#ffeedd';
    ctx.beginPath(); ctx.arc(-4, -4, 4, 0, Math.PI * 2); ctx.arc(4, -4, 4, 0, Math.PI * 2); ctx.fill();
    if (this.type === 'shield') {
      ctx.strokeStyle = '#aaccff'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(0, 0, this.size + 8, 0, Math.PI * 2); ctx.stroke();
    }
    if (this.type === 'chaos') {
      ctx.fillStyle = '#ffff00';
      ctx.beginPath();
      ctx.moveTo(0, -this.size * 0.6);
      ctx.lineTo(this.size * 0.6, this.size * 0.6);
      ctx.lineTo(-this.size * 0.6, this.size * 0.6);
      ctx.closePath(); ctx.fill();
    }
    ctx.restore();
  }
}

class Mutant {
  constructor(x, y) {
    this.x = x; this.y = y; this.size = 28;
    this.vx = (Math.random() - 0.5) * 1.5;
    this.vy = (Math.random() - 0.5) * 1.5;
    this.pulse = 0; this.hits = 3;
  }
  update(cx, cy, radius, delta) {
    const dx = cx - this.x, dy = cy - this.y;
    const dist = Math.hypot(dx, dy);
    this.vx += (dx / dist) * 0.8; this.vy += (dy / dist) * 0.8;
    const vel = Math.hypot(this.vx, this.vy);
    if (vel > 3) { this.vx *= 0.85; this.vy *= 0.85; }
    this.x += this.vx * (delta / 16);
    this.y += this.vy * (delta / 16);
    this.pulse = Math.sin(Date.now() / 80) * 4;
  }
  draw(ctx) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.shadowColor = '#ff0088'; ctx.shadowBlur = 40;
    ctx.fillStyle = '#aa0088';
    ctx.beginPath(); ctx.arc(0, 0, this.size + this.pulse, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#ff88cc'; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.arc(0, 0, this.size * 0.7, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────
const AlienSwarm = ({ userName }) => {
  const { isNewRecord, leaderboard, submitScore, fetchLeaderboard } = useScoreSubmit('alienswarm');
  const canvasRef      = useRef(null);
  const animationRef   = useRef(null);
  const [gameState, setGameState] = useState('menu');
  const [helpOpen, setHelpOpen]   = useState(false);

  // ALL game state lives in one ref — no stale closures in the rAF loop
  const gv = useRef({
    // live game values (written + read inside loop)
    score: 0, combo: 0, level: 1, highScore: 0,
    overload: 0, spawnTimer: 0, comboTimer: 0,
    totalAbsorbed: 0, lastTime: 0,
    unlockedAliens: new Set(['normal']),
    // objects
    aliens: [], particles: [], mutants: [], floatingTexts: [],
    // arena geometry (set on start)
    portalX: 0, portalY: 0, radius: 0,
    // touch
    swipeStart: null, swipeActive: false,
    // display-only state callback (to trigger React re-render for HUD)
    onHUDUpdate: null,
  });

  // Thin React state only for things that affect JSX outside canvas
  const [hud, setHUD] = useState({ score: 0, combo: 0, level: 1, overload: 0 });
  const [unlockedAliens, setUnlockedAliens] = useState(new Set(['normal']));

  // ── helpers ────────────────────────────────────────────────────────────────
  const createExplosion = useCallback((x, y, color, count = 22) => {
    const g = gv.current;
    for (let i = 0; i < count; i++)
      g.particles.push(new Particle(x, y, color, (Math.random() - 0.5) * 9, (Math.random() - 0.5) * 9));
  }, []);

  const scaleCoords = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect   = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  }, []);

  const spawnAlien = useCallback(() => {
    const g = gv.current;
    const angle     = Math.random() * Math.PI * 2;
    const spawnDist = g.radius * 0.92;
    const x = g.portalX + Math.cos(angle) * spawnDist;
    const y = g.portalY + Math.sin(angle) * spawnDist;

    const rand = Math.random(), lvl = g.level;
    let type = 'normal';
    if      (lvl >= 5 && rand < 0.20) type = 'chaos';
    else if (lvl >= 4 && rand < 0.35) type = 'shield';
    else if (lvl >= 3 && rand < 0.50) type = 'split';
    else if (lvl >= 2 && rand < 0.65) type = 'fast';

    g.aliens.push(new Alien(x, y, type));
  }, []);

  const absorbAlien = useCallback((idx) => {
    const g = gv.current;
    const alien = g.aliens[idx];
    if (!alien) return;

    const t = ALIEN_TYPES[alien.type];
    g.combo++;
    g.comboTimer = 120;
    g.totalAbsorbed++;
    g.overload = Math.max(0, g.overload - 7);

    let pts = t.points;
    if (g.combo > 8) pts = Math.floor(pts * 1.6);
    g.score += pts;

    // floating text
    g.floatingTexts.push(new FloatingText(
      alien.x, alien.y - 10,
      g.combo > 2 ? `+${pts} ×${g.combo}` : `+${pts}`,
      g.combo > 8 ? '#ffcc00' : t.color
    ));

    // sound
    if (g.combo > 2) playCombo(g.combo); else playKill();

    // unlock
    if (!g.unlockedAliens.has(alien.type)) {
      g.unlockedAliens = new Set([...g.unlockedAliens, alien.type]);
      setUnlockedAliens(new Set(g.unlockedAliens));
    }

    // Venom split
    if (alien.type === 'split' && g.aliens.length < LEVELS[g.level - 1].maxAliens) {
      [1, -1].forEach(dir => {
        const nx = alien.x + dir * 22, ny = alien.y + dir * 10;
        g.aliens.push(new Alien(nx, ny, 'fast'));
      });
    }

    createExplosion(alien.x, alien.y, t.color, 18);
    g.aliens.splice(idx, 1);

    // level check
    const goal = LEVELS[Math.min(g.level - 1, 4)].goal;
    if (g.score >= goal && g.level < 5) {
      g.level++;
      playLevelUp();
      g.floatingTexts.push(new FloatingText(g.portalX, g.portalY - 60, `LEVEL ${g.level}!`, '#00ffcc'));
    }

    setHUD({ score: g.score, combo: g.combo, level: g.level, overload: g.overload });
  }, [createExplosion]);

  const checkCollisions = useCallback(() => {
    const g = gv.current;
    outer:
    for (let i = g.aliens.length - 1; i >= 0; i--) {
      for (let j = i - 1; j >= 0; j--) {
        const a1 = g.aliens[i], a2 = g.aliens[j];
        const dx = a1.x - a2.x, dy = a1.y - a2.y;
        if (dx * dx + dy * dy < (a1.size + a2.size + 8) ** 2) {
          for (let k = 0; k < g.aliens.length; k++) {
            if (k === i || k === j) continue;
            const a3 = g.aliens[k];
            if ((a1.x - a3.x) ** 2 + (a1.y - a3.y) ** 2 < 2200) {
              const mx = (a1.x + a2.x + a3.x) / 3;
              const my = (a1.y + a2.y + a3.y) / 3;
              g.mutants.push(new Mutant(mx, my));
              // remove highest index first to keep lower indices valid
              const toRemove = [i, j, k].sort((a, b) => b - a);
              toRemove.forEach(idx => g.aliens.splice(idx, 1));
              createExplosion(mx, my, '#ff0088', 28);
              playMutant();
              break outer;
            }
          }
        }
      }
    }
  }, [createExplosion]);

  const endGame = useCallback(() => {
    cancelAnimationFrame(animationRef.current);
    const g = gv.current;
    g.highScore = Math.max(g.highScore, g.score);
    setGameState('gameover');

    const timeTakenSec = Math.round((Date.now() - (g.matchStartedAt || Date.now())) / 1000);
    submitScore({ score: g.score, timeTakenSec, levelReached: g.level });
  }, [submitScore]);

  // ── game loop ───────────────────────────────────────────────────────────────
  const gameLoop = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const g   = gv.current;
    const W = canvas.width, H = canvas.height;
    const delta = Math.min(timestamp - (g.lastTime || timestamp), 50);
    g.lastTime = timestamp;

    // ── clear ──
    ctx.fillStyle = '#070714';
    ctx.fillRect(0, 0, W, H);

    // ── arena ring ──
    ctx.strokeStyle = '#1e2d4a';
    ctx.lineWidth = Math.max(10, g.radius * 0.05);
    ctx.beginPath(); ctx.arc(g.portalX, g.portalY, g.radius, 0, Math.PI * 2); ctx.stroke();

    // subtle grid lines inside arena for depth
    ctx.save();
    ctx.globalAlpha = 0.06;
    ctx.strokeStyle = '#44aaff';
    ctx.lineWidth = 1;
    for (let a = 0; a < Math.PI * 2; a += Math.PI / 6) {
      ctx.beginPath();
      ctx.moveTo(g.portalX, g.portalY);
      ctx.lineTo(g.portalX + Math.cos(a) * g.radius, g.portalY + Math.sin(a) * g.radius);
      ctx.stroke();
    }
    ctx.restore();

    // ── portal ──
    ctx.shadowBlur = 50; ctx.shadowColor = '#4466ff';
    ctx.fillStyle = '#0d1a33';
    ctx.beginPath(); ctx.arc(g.portalX, g.portalY, 40, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 20;
    ctx.fillStyle = '#66bbff';
    ctx.beginPath(); ctx.arc(g.portalX, g.portalY, 24, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;

    // ── entities ──
    g.aliens.forEach(a => { a.update(g.portalX, g.portalY, g.radius, delta, g.overload); a.draw(ctx); });
    g.mutants.forEach(m => { m.update(g.portalX, g.portalY, g.radius, delta); m.draw(ctx); });

    for (let i = g.particles.length - 1; i >= 0; i--) {
      g.particles[i].update(); g.particles[i].draw(ctx);
      if (g.particles[i].life <= 0) g.particles.splice(i, 1);
    }
    for (let i = g.floatingTexts.length - 1; i >= 0; i--) {
      g.floatingTexts[i].update(); g.floatingTexts[i].draw(ctx);
      if (g.floatingTexts[i].life <= 0) g.floatingTexts.splice(i, 1);
    }

    // ── swipe preview ──
    if (g.swipeActive && g.swipeStart) {
      ctx.save();
      ctx.strokeStyle = 'rgba(136,255,255,0.45)';
      ctx.lineWidth = 5; ctx.setLineDash([10, 7]);
      ctx.beginPath();
      ctx.moveTo(g.swipeStart.x, g.swipeStart.y);
      ctx.lineTo(
        g.portalX + (g.swipeStart.x - g.portalX) * 2.5,
        g.portalY + (g.swipeStart.y - g.portalY) * 2.5
      );
      ctx.stroke(); ctx.setLineDash([]);
      ctx.restore();
    }

    // ── combo overlay on canvas ──
    if (g.combo > 2) {
      const size = Math.min(48, 28 + g.combo * 1.1);
      ctx.save();
      ctx.font = `bold ${size}px monospace`;
      ctx.textAlign = 'center';
      ctx.fillStyle = g.combo > 15 ? '#ffcc00' : '#aaffff';
      ctx.shadowColor = g.combo > 15 ? '#ffaa00' : '#00ffff';
      ctx.shadowBlur = 18;
      ctx.globalAlpha = 0.88;
      ctx.fillText(`${g.combo}×`, g.portalX, g.portalY - 60);
      ctx.restore();
    }

    // ── spawn logic ──
    g.spawnTimer -= delta;
    const ld = LEVELS[Math.min(g.level - 1, 4)];
    if (g.spawnTimer <= 0 && g.aliens.length < ld.maxAliens) {
      spawnAlien();
      g.spawnTimer = ld.spawnRate * (0.6 + Math.random() * 0.8);
      if (g.overload > 65) g.spawnTimer *= 0.4;
    }

    // ── overload ──
    if (g.aliens.length > 4) g.overload = Math.min(100, g.overload + (g.aliens.length - 3) * 0.028);
    else                      g.overload = Math.max(0, g.overload - 0.2);

    // ── combo decay ──
    if (g.comboTimer > 0) g.comboTimer -= delta / 16;
    else if (g.combo > 0) { g.combo = 0; setHUD(h => ({ ...h, combo: 0 })); }

    checkCollisions();

    // ── HUD update (throttled — every ~10 frames) ──
    if (Math.floor(timestamp / 160) !== Math.floor((timestamp - delta) / 160))
      setHUD({ score: g.score, combo: g.combo, level: g.level, overload: g.overload });

    // ── game over condition ──
    if (g.mutants.length > 4 || g.aliens.length > ld.maxAliens + 12) { endGame(); return; }

    animationRef.current = requestAnimationFrame(gameLoop);
  }, [spawnAlien, checkCollisions, endGame]);

  // ── start game ─────────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // make canvas internal resolution match its CSS display size
    const rect = canvas.getBoundingClientRect();
    const size = Math.round(rect.width);
    canvas.width  = size;
    canvas.height = size;

    const g = gv.current;
    g.matchStartedAt = Date.now();
    g.score = 0; g.combo = 0; g.level = 1; g.overload = 12;
    g.spawnTimer = 400; g.comboTimer = 0; g.totalAbsorbed = 0; g.lastTime = 0;
    g.aliens = []; g.mutants = []; g.particles = []; g.floatingTexts = [];
    g.unlockedAliens = new Set(['normal']);
    g.portalX = size / 2; g.portalY = size / 2; g.radius = size * 0.44;
    g.swipeStart = null; g.swipeActive = false;

    setHUD({ score: 0, combo: 0, level: 1, overload: 0 });
    setUnlockedAliens(new Set(['normal']));
    setGameState('playing');

    cancelAnimationFrame(animationRef.current);
    for (let i = 0; i < 3; i++) spawnAlien();
    g.lastTime = performance.now();
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [spawnAlien, gameLoop]);

  // ── input handlers ──────────────────────────────────────────────────────────
  const findClosestAlien = useCallback((sx, sy) => {
    const g = gv.current;
    let idx = -1, minDist = Infinity;
    for (let i = 0; i < g.aliens.length; i++) {
      const a = g.aliens[i];
      const d = Math.hypot(a.x - sx, a.y - sy);
      if (d < a.size + 38 && d < minDist) { minDist = d; idx = i; }
    }
    return idx;
  }, []);

  const handleCanvasClick = useCallback((e) => {
    if (gameState !== 'playing') return;
    const { x, y } = scaleCoords(e.clientX, e.clientY);
    const idx = findClosestAlien(x, y);
    if (idx !== -1) absorbAlien(idx);
  }, [gameState, scaleCoords, findClosestAlien, absorbAlien]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    if (gameState !== 'playing') return;
    const touch = e.touches[0];
    const { x, y } = scaleCoords(touch.clientX, touch.clientY);
    gv.current.swipeStart = { x, y, time: Date.now() };
    gv.current.swipeActive = true;
  }, [gameState, scaleCoords]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    if (gameState !== 'playing' || !gv.current.swipeStart) return;
    const touch = e.changedTouches[0];
    const { x: endX, y: endY } = scaleCoords(touch.clientX, touch.clientY);
    const start = gv.current.swipeStart;
    const dx = endX - start.x, dy = endY - start.y;
    const dist = Math.hypot(dx, dy);
    const dt = Date.now() - start.time;

    if (dist < 18 && dt < 300) {
      // it's a tap — treat as click
      const idx = findClosestAlien(start.x, start.y);
      if (idx !== -1) absorbAlien(idx);
    } else if (dist >= 45) {
      // it's a swipe
      const angle = Math.atan2(dy, dx);
      const g = gv.current;
      let count = 0;
      for (let i = g.aliens.length - 1; i >= 0; i--) {
        const a = g.aliens[i];
        const ax = a.x - start.x, ay = a.y - start.y;
        const alienDist = Math.hypot(ax, ay);
        const alienAngle = Math.atan2(ay, ax);
        // normalize angle difference to [-π, π]
        let diff = alienAngle - angle;
        while (diff > Math.PI)  diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        if (Math.abs(diff) < 0.55 && alienDist < 450) {
          createExplosion(a.x, a.y, '#88ffff', 12);
          g.floatingTexts.push(new FloatingText(a.x, a.y, `+${ALIEN_TYPES[a.type].points}`, '#88ffff'));
          g.aliens.splice(i, 1);
          count++;
        }
      }
      if (count > 0) {
        g.score += count * 8;
        g.combo = Math.max(g.combo, g.combo + Math.floor(count / 2));
        g.overload = Math.max(0, g.overload - count * 4);
        playKill();
        setHUD({ score: g.score, combo: g.combo, level: g.level, overload: g.overload });
      }
    }

    gv.current.swipeStart  = null;
    gv.current.swipeActive = false;
  }, [gameState, scaleCoords, findClosestAlien, absorbAlien, createExplosion]);

  // ── keyboard ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ') {
        if (gameState === 'playing') {
          cancelAnimationFrame(animationRef.current);
          setGameState('paused');
        } else if (gameState === 'paused') {
          gv.current.lastTime = performance.now();
          setGameState('playing');
          animationRef.current = requestAnimationFrame(gameLoop);
        }
      }
      if (e.key.toLowerCase() === 'r' && gameState === 'gameover') startGame();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gameState, gameLoop, startGame]);

  // ── prevent page scroll while playing ──────────────────────────────────────
  useEffect(() => {
    const prevent = (e) => { if (gameState === 'playing') e.preventDefault(); };
    document.body.style.touchAction = gameState === 'playing' ? 'none' : '';
    window.addEventListener('touchmove', prevent, { passive: false });
    return () => { window.removeEventListener('touchmove', prevent); document.body.style.touchAction = ''; };
  }, [gameState]);

  // ── cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => () => cancelAnimationFrame(animationRef.current), []);

  // ── derived ─────────────────────────────────────────────────────────────────
  const overloadPct  = Math.round(hud.overload);
  const overloadDanger = overloadPct > 75;
  const levelData    = LEVELS[Math.min((hud.level || 1) - 1, 4)];
  const levelGoal    = levelData.goal === Infinity ? '∞' : levelData.goal.toLocaleString();

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center min-h-screen w-full bg-[#07071a] font-mono text-[#ccffee] select-none overflow-x-hidden border-b-2 border-gray-300 rounded-xl">
      <h1 className="text-base lg:text-2xl tracking-[5px] text-yellow-300 mt-3 mb-1 text-center">
        👾 ALIEN SWARM
      </h1>
      {userName && (
        <p className="text-[10px] lg:text-xs text-gray-200 mb-1 tracking-widest">
          COMMANDER {userName.toUpperCase()}
        </p>
      )}

      {/* ── Evolution strip ── */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center items-center
                      bg-[#0d0d2b] border border-[#1e2d50] rounded-lg
                      px-3 py-1.5 mb-2 max-w-[min(92vw,600px)]">
        <span className="text-[10px] lg:text-xs text-teal-400 tracking-widest mr-1">EVOLUTION</span>
        {Object.entries(ALIEN_TYPES).map(([type, info]) => (
          <span
            key={type}
            className={`text-[10px] lg:text-xs transition-all duration-300 ${
              unlockedAliens.has(type) ? 'opacity-100' : 'opacity-30 grayscale'
            }`}
            style={{ color: unlockedAliens.has(type) ? info.color : '#555' }}
          >
            👾 {info.name}
          </span>
        ))}
      </div>

      {/* ── HUD strip ── */}
      <div className="flex items-center justify-between gap-2
                      w-full max-w-[min(92vw,600px)] mb-2 px-1">
        {/* Score */}
        <div className="flex flex-col items-start">
          <span className="text-[9px] lg:text-[11px] text-gray-200 tracking-widest">SCORE</span>
          <span className="text-sm lg:text-xl font-bold text-white tabular-nums">
            {hud.score.toString().padStart(6, '0')}
          </span>
        </div>

        {/* Level + goal */}
        <div className="flex flex-col items-center">
          <span className="text-[9px] lg:text-[11px] text-gray-200 tracking-widest">LEVEL</span>
          <span className="text-sm lg:text-xl font-bold text-cyan-300">{hud.level} / 5</span>
          <span className="text-[8px] lg:text-[10px] text-gray-300">
            goal {levelGoal}
          </span>
        </div>

        {/* Overload bar */}
        <div className="flex flex-col items-end gap-1">
          <span className={`text-[9px] lg:text-[11px] tracking-widest ${overloadDanger ? 'text-red-400 animate-pulse' : 'text-gray-200'}`}>
            OVERLOAD {overloadPct}%
          </span>
          <div className="w-24 lg:w-36 h-2 bg-[#1a1a3a] rounded-full overflow-hidden border border-[#334]">
            <div
              className={`h-full rounded-full transition-all duration-300 ${overloadDanger ? 'bg-red-500' : 'bg-cyan-400'}`}
              style={{ width: `${overloadPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Canvas container ── */}
      <div className="relative" style={{ width: 'min(92vw, 600px)', height: 'min(92vw, 600px)' }}>
        {/* outer glow ring */}
        <div className="absolute inset-0 rounded-full pointer-events-none"
             style={{ boxShadow: '0 0 60px rgba(68,0,170,0.5), inset 0 0 30px rgba(0,0,0,0.8)', borderRadius: '50%' }} />
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="rounded-full touch-none block"
          style={{
            width: '100%', height: '100%',
            cursor: gameState === 'playing' ? 'crosshair' : 'default',
            background: '#070714',
          }}
        />

        {/* ── Overlays (menu / paused / gameover) ── */}
        {gameState === 'menu' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-full
                          bg-[rgba(7,7,26,0.82)]">
            <p className="text-cyan-300 text-xs lg:text-sm tracking-widest mb-1">TAP ALIENS TO ABSORB</p>
            <p className="text-gray-200 text-[10px] lg:text-xs mb-5">SWIPE TO HIT MULTIPLE</p>
            <button
              onClick={startGame}
              className="px-6 py-2 lg:px-10 lg:py-3 text-sm lg:text-base
                         bg-gradient-to-b from-[#5500ff] to-[#aa00ff]
                         text-white rounded-full font-bold tracking-widest
                         shadow-[0_0_30px_#9900ff] active:scale-95 transition-transform"
            >
              START SWARM
            </button>
            <button
              onClick={() => setHelpOpen(true)}
              className="mt-3 text-[10px] lg:text-xs text-gray-200 underline underline-offset-2"
            >
              HOW TO PLAY
            </button>
          </div>
        )}

        {gameState === 'paused' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-full
                          bg-[rgba(7,7,26,0.88)]">
            <h2 className="text-cyan-200 text-lg lg:text-2xl font-bold tracking-widest mb-2">PAUSED</h2>
            <p className="text-gray-100 text-xs lg:text-sm mb-4">SPACE to resume • R to restart</p>
            <button
              onClick={() => {
                gv.current.lastTime = performance.now();
                setGameState('playing');
                animationRef.current = requestAnimationFrame(gameLoop);
              }}
              className="px-6 py-2 text-sm bg-[#223355] text-cyan-200 border border-cyan-700
                         rounded-full active:scale-95 transition-transform"
            >
              RESUME
            </button>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-full
                          bg-[rgba(20,5,25,0.92)]">
            <h2 className="text-pink-400 text-lg lg:text-2xl font-bold tracking-wider mb-1">
              SWARM OVERLOAD
            </h2>
            <p className="text-gray-200 text-[10px] lg:text-xs mb-1 tracking-widest">FINAL SCORE</p>
            <p className="text-white text-2xl lg:text-4xl font-bold tabular-nums mb-1">
              {hud.score.toLocaleString()}
            </p>
            {gv.current.highScore > 0 && (
              <p className="text-yellow-400 text-[10px] lg:text-xs mb-4">
                BEST: {gv.current.highScore.toLocaleString()}
              </p>
            )}
            <p className="text-gray-200 text-[10px] lg:text-xs mb-4">
              Reached level {hud.level} · {gv.current.totalAbsorbed} absorbed
            </p>
            <ScoreboardPanel
              score={hud.score}
              isNewRecord={isNewRecord}
              leaderboard={leaderboard}
              fetchLeaderboard={fetchLeaderboard}
            />
            <button
              onClick={startGame}
              className="px-8 py-2 text-sm bg-pink-600 hover:bg-pink-500
                         text-white rounded-xl font-bold tracking-widest
                         active:scale-95 transition-all"
            >
              TRY AGAIN
            </button>
          </div>
        )}
      </div>

      {/* ── Help toggle (below canvas during play) ── */}
      {gameState === 'playing' && (
        <button
          onClick={() => setHelpOpen(o => !o)}
          className="mt-2 text-[10px] lg:text-xs text-gray-200 underline underline-offset-2"
        >
          {helpOpen ? 'HIDE HELP' : '? HOW TO PLAY'}
        </button>
      )}

      {/* ── Help panel ── */}
      {helpOpen && (
        <div className="mt-2 mb-4 max-w-[min(92vw,600px)] w-full
                        bg-[#0d0d2a] border border-[#1e2d50] rounded-xl p-4">
          <div className="flex justify-between items-start mb-3">
            <h2 className="text-cyan-200 text-sm lg:text-lg font-bold tracking-wide">How to Play</h2>
            <button onClick={() => setHelpOpen(false)} className="w-fit px-2 py-1 text-gray-200 text-xs lg:text-sm">✕</button>
          </div>

          <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold mb-1 mt-2">Controls</h2>
          <ul className="space-y-1 mb-3">
            <li className="text-gray-100 text-xs lg:text-sm">
              <span className="text-cyan-400">Tap / Click</span> an alien to absorb it
            </li>
            <li className="text-gray-100 text-xs lg:text-sm">
              <span className="text-cyan-400">Swipe</span> across the arena to hit multiple aliens in a line
            </li>
            <li className="text-gray-200 text-xs lg:text-sm">
              <span className="text-cyan-400">SPACE</span> to pause (desktop)
            </li>
          </ul>

          <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold mb-1">Alien Types</h2>
          <ul className="space-y-1 mb-3">
            {Object.entries(ALIEN_TYPES).map(([type, info]) => (
              <li key={type} className="text-gray-100 text-xs lg:text-sm flex items-center gap-2">
                <span style={{ color: info.color }} className="font-bold w-12 shrink-0">{info.name}</span>
                <span className="text-gray-100">
                  {type === 'normal' && 'Standard alien — easy to absorb'}
                  {type === 'fast'   && 'Moves quickly — tap fast!'}
                  {type === 'split'  && 'Splits into 2 Sparks when absorbed — beware!'}
                  {type === 'shield' && 'Armoured — takes 2 hits, worth more points'}
                  {type === 'chaos'  && 'Fastest and highest scoring — unlocks at level 5'}
                </span>
              </li>
            ))}
          </ul>

          <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold mb-1">Danger Zones</h2>
          <ul className="space-y-1 mb-3">
            <li className="text-gray-100 text-xs lg:text-sm">
              <span className="text-pink-400">Mutants</span> form when 3 aliens collide — they're big, fast, and dangerous
            </li>
            <li className="text-gray-100 text-xs lg:text-sm">
              <span className="text-red-400">Overload bar</span> fills as aliens pile up — game over if it stays maxed
            </li>
            <li className="text-gray-200 text-xs lg:text-sm">
              Game ends if you have more than 4 mutants alive simultaneously
            </li>
          </ul>

          <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold mb-1">Scoring</h2>
          <ul className="space-y-1">
            <li className="text-gray-100 text-xs lg:text-sm">Build a <span className="text-yellow-300">combo</span> by absorbing aliens quickly — bonus ×1.6 at 8+</li>
            <li className="text-gray-100 text-xs lg:text-sm">5 levels, each needing a higher score goal to advance</li>
            <li className="text-gray-200 text-xs lg:text-sm">New alien types unlock as you level up — score bonus increases</li>
          </ul>
        </div>
      )}

      {/* ── High score footer ── */}
      {gv.current.highScore > 0 && gameState !== 'playing' && (
        <p className="text-[10px] lg:text-xs text-yellow-500 mt-2 mb-4 tracking-widest">
          BEST: {gv.current.highScore.toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default AlienSwarm;
